import PropTypes from 'prop-types';
import GenericInteraction from 'client/js/resellercp/generic_resource/interaction';
import { StringInput, RadioCollectionInput, SliderInput } from 'client/js/util/form_utils';
import { toast } from 'react-toastify';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from 'client/js/util/layout_utils';
import LoadingTableSpinner from 'client/js/util/loading_table_spinner';
import { errorToComponent } from 'client/js/util/rest_utils';
import React, { useState, useEffect, useRef } from 'react';
import { ControlErrors } from 'client/js/util/form_utils';
import Modal, { ModalHeader } from 'client/js/util/modal';
import { FaIcon } from 'client/js/util/layout_utils';

const fetchData = async (id) => {
  const response = await $.ajax({ url: `/usercp/domains/${id}`, dataType: 'json' });
  return response;
}

// Renamed component to display existing key data
const DnssecKeyDisplayRow = ({ /* index, */ keyData, onRemove, onEdit }) => {
  return (
    // Use table row and cells
    <tr>
      <td>{keyData?.flags || '-'}</td>
      <td>{keyData?.protocol || '-'}</td>
      <td>{keyData?.algorithm || '-'}</td>
      <td className="text-truncate" style={{ maxWidth: '150px' }} title={keyData?.public_key}>{keyData?.public_key || '-'}</td>
      <td className="text-right">
        <div className="btn-group btn-group-sm">
          <button
            type="button"
            className="btn btn-light"
            onClick={onEdit} // Call onEdit passed from Form
            title="Diesen Schlüssel bearbeiten"
          >
            <FaIcon name="pencil" />
          </button>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={onRemove} // Use the passed onRemove handler directly
            title="Diesen Schlüssel entfernen"
          >
            <FaIcon name="trash" />
          </button>
        </div>
      </td>
    </tr>
  );
};

DnssecKeyDisplayRow.propTypes = {
  keyData: PropTypes.object, // Key data to display
  onRemove: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired // Callback for edit action
};

// --- DnssecKeyModal Component (Placeholder for now) ---
const DnssecKeyModal = ({ isOpen, onRequestClose, initialData, onSave }) => {
  // Ref for the modal's form element
  const formRef = useRef(null);

  // Local state for the modal form
  const [flags, setFlags] = useState('');
  const [protocol, setProtocol] = useState('');
  const [algorithm, setAlgorithm] = useState('');
  const [publicKey, setPublicKey] = useState('');

  // Initialize/reset state when modal opens or initialData changes
  useEffect(() => {
    if (isOpen) {
      setFlags(initialData?.flags || '');
      setProtocol(initialData?.protocol || '');
      setAlgorithm(initialData?.algorithm || '');
      setPublicKey(initialData?.public_key || '');
    } else {
      // Optional: Reset when closing (though initialData change handles reopening)
      setFlags('');
      setProtocol('');
      setAlgorithm('');
      setPublicKey('');
    }
  }, [isOpen, initialData]);

  const handleSaveClick = () => {
    // Manually trigger validation and REPORT the result (shows UI feedback)
    if (!formRef.current?.reportValidity()) {
      return; // Stop if form is invalid
    }

    const savedKeyData = {
      flags: parseInt(flags, 10) || 0, // Ensure integer conversion
      protocol: parseInt(protocol, 10) || 0,
      algorithm: parseInt(algorithm, 10) || 0,
      public_key: publicKey
    };
    onSave(savedKeyData);
    // onRequestClose(); // Let the onSave handler (handleSaveKey) close it
  };

  // Basic form submission prevention (optional, as buttons are type="button")
  const handleSubmit = (e) => {
    e.preventDefault();
    handleSaveClick();
  };

  // --- Conditional return MUST be AFTER hooks ---
  if (!isOpen) return null;

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} className="modal fade in show d-block modal-no-borders" contentLabel="DNSSEC Schlüssel bearbeiten">
      <ModalHeader title={initialData ? "DNSSEC Schlüssel bearbeiten" : "DNSSEC Schlüssel hinzufügen"} onRequestClose={onRequestClose} />
      <form ref={formRef} onSubmit={handleSubmit}>
        <div className="modal-body">
          {/* Flags Input */}
          <div className="form-group">
            <label htmlFor="modal_flags">Flags</label>
            <input
              id="modal_flags"
              type="number"
              className="form-control"
              value={flags}
              onChange={(e) => setFlags(e.target.value)}
              required
              step="1"
              min="256"
              max="257"
            />
             <small className="form-text text-muted">256 oder 257</small>
          </div>

          {/* Protocol Input */}
          <div className="form-group">
            <label htmlFor="modal_protocol">Protokoll</label>
            <input
              id="modal_protocol"
              type="number"
              className="form-control"
              value={protocol}
              onChange={(e) => setProtocol(e.target.value)}
              required
              step="1"
              min="3"
              max="3"
            />
             <small className="form-text text-muted">Muss 3 sein</small>
          </div>

          {/* Algorithm Input */}
          <div className="form-group">
            <label htmlFor="modal_algorithm">Algorithmus</label>
            <input
              id="modal_algorithm"
              type="number"
              className="form-control"
              value={algorithm}
              onChange={(e) => setAlgorithm(e.target.value)}
              required
              step="1"
              min="1"
              max="16"
            />
            <small className="form-text text-muted">1 bis 16</small>
          </div>

          {/* Public Key Input */}
          <div className="form-group">
            <label htmlFor="modal_public_key">Öffentlicher Schlüssel</label>
            <textarea
              id="modal_public_key"
              className="form-control"
              rows="4"
              value={publicKey}
              onChange={(e) => setPublicKey(e.target.value)}
              required
            />
            <small className="form-text text-muted">Base64 kodiert</small>
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onRequestClose}>Abbrechen</button>
          <button type="button" className="btn btn-primary" onClick={handleSaveClick}>Hinzufügen</button> {/* Use type="button" and onClick */}
        </div>
      </form>
    </Modal>
  );
};

DnssecKeyModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onRequestClose: PropTypes.func.isRequired,
  initialData: PropTypes.object, // null for add, object for edit
  onSave: PropTypes.func.isRequired
};

const Form = (props, context) => {
  const { formData, formDispatch, serverErrors } = context;
  const dnssecKeys = formData?.dnssec_keys || [];

  // State to control DNSSEC section visibility
  const [showDnssec, setShowDnssec] = useState(dnssecKeys.length > 0);

  // State for modal management
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingKeyIndex, setEditingKeyIndex] = useState(null); // null for add, index for edit

  // Effect to update visibility if keys are added/removed externally or on load
  useEffect(() => {
    setShowDnssec(dnssecKeys.length > 0);
  }, [dnssecKeys]);

  const handleToggleDnssec = (e) => {
    const shouldShow = e.target.checked;
    setShowDnssec(shouldShow);
    // If turning off DNSSEC, clear the keys from form data
    if (!shouldShow) {
      formDispatch('value_changed', null, { name: 'dnssec_keys', value: [] });
    }
  };

  const handleAddKey = () => {
    if (dnssecKeys.length >= 3) return; // Don't open modal if max keys reached
    setEditingKeyIndex(null); // Signal add mode
    setIsModalOpen(true);
  };

  const handleRemoveKey = (indexToRemove) => {
    const newKeysArray = dnssecKeys.filter((_, index) => index !== indexToRemove);
    formDispatch('value_changed', null, { name: 'dnssec_keys', value: newKeysArray });
  };

  const handleEditKey = (indexToEdit) => {
    setEditingKeyIndex(indexToEdit);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingKeyIndex(null);
  };

  const handleSaveKey = (savedKeyData) => {
    const currentKeys = formData?.dnssec_keys || [];
    let updatedKeys;

    if (editingKeyIndex !== null) {
      // Update existing key
      updatedKeys = currentKeys.map((key, index) =>
        index === editingKeyIndex ? savedKeyData : key
      );
    } else {
      // Add new key
      updatedKeys = [...currentKeys, savedKeyData];
    }

    formDispatch('value_changed', null, { name: 'dnssec_keys', value: updatedKeys });
    handleCloseModal(); // Close modal after saving
  };

  return (
    <>
      <RadioCollectionInput
        required
        name="dns_mode"
        label="DNS-Modus"
        options={{
          'auto': 'Nameserver von lima-city verwenden',
          'custom': 'Eigene Nameserver angeben'
        }}
      />

      <StringInput disabled={formData?.dns_mode == 'auto'} required name="nameserver1" label="Nameserver" />
      <StringInput disabled={formData?.dns_mode == 'auto'} required name="nameserver2" label="Nameserver" />
      <StringInput disabled={formData?.dns_mode == 'auto'} name="nameserver3" label="Nameserver" />
      <StringInput disabled={formData?.dns_mode == 'auto'} name="nameserver4" label="Nameserver" />
      <StringInput disabled={formData?.dns_mode == 'auto'} name="nameserver5" label="Nameserver" />

      {formData?.dns_mode === 'custom' && (
        <div className="form-group mt-4">
          <SliderInput label="DNSSEC aktivieren" name="dnssec_enabled" hint="DNSSEC verbessert die Sicherheit des DNS durch kryptografische Signierung. Um DNS-Einträge überprüfbar zu machen müssen wir die DNSSEC-Parameter des DNS-Servers bei der Registry hinterlegen. Diese können hier eingetragen werden." checked={showDnssec} onChange={handleToggleDnssec} />

          {/* --- Conditionally Rendered DNSSEC Section --- */}
          {showDnssec && (
            <div className="row"> {/* Wrap content in row */}
              <div className="col-md-9 offset-md-3"> {/* Apply offset */}
                <hr className="my-4" />
                <h5 className="mb-2">DNSSEC-Schlüssel</h5>

                {/* Display top-level errors for the dnssec_keys field */}
                <ControlErrors errors={serverErrors?.dnssec_keys} />

                {/* Wrap keys in a table */}
                {dnssecKeys.length > 0 && (
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{width: '15%'}}>Flags</th>
                        <th style={{width: '15%'}}>Protokoll</th>
                        <th style={{width: '15%'}}>Algorithmus</th>
                        <th style={{width: '40%'}}>Öffentlicher Schlüssel</th>
                        <th style={{width: '15%'}} className="text-right">Aktionen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dnssecKeys.map((key, index) => (
                        <DnssecKeyDisplayRow
                          key={`${key.flags}-${key.protocol}-${key.algorithm}-${key.public_key}`} // Reverted key to not use index
                          index={index}
                          keyData={key}
                          onRemove={() => handleRemoveKey(index)}
                          onEdit={() => handleEditKey(index)} // Pass edit handler
                        />
                      ))}
                    </tbody>
                  </table>
                )}

                {/* Add Key Button - moved below the list */}
                {dnssecKeys.length < 3 && (
                  <button
                    type="button"
                    className="btn btn-light btn-sm mt-2"
                    onClick={handleAddKey} // Opens the modal
                  >
                    <FaIcon name="plus" /> Schlüssel hinzufügen
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Render the Modal outside the conditional DNSSEC section, controlled by Form state */}
      <DnssecKeyModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        onSave={handleSaveKey}
        // Pass initial data for editing, or null for adding
        initialData={editingKeyIndex !== null ? dnssecKeys[editingKeyIndex] : null}
      />
    </>
  );
};

Form.contextTypes = {
  formData: PropTypes.object,
  formDispatch: PropTypes.func,
  serverErrors: PropTypes.object,
  fieldsValidated: PropTypes.array,
  namespace: PropTypes.string
};

const onSuccess = () => {
  toast.success("DNS-Einstellungen gespeichert");
};

const onValidation = (formData) => {
  const errors = {};
  if(formData?.dnssec_enabled && formData?.dnssec_keys.length == 0) {
    errors.dnssec_keys = ["DNSSEC aktiviert, aber kein Schlüssel hinterlegt"];
  }
  return errors;
};

const UpdateDnsServers = (props) => {
  const id = props.match.params.domain_id;

  const { data, isLoading, isError, error } = useQuery(['domain', id], () => fetchData(id), {
    keepPreviousData: true
  });

  if(isLoading) {
    return (
      <>
        <PageHeader back_url={`/domains/${id}`} text={i18n_t(`resources.domains.interactions.update_dns_servers`)} />
        <LoadingTableSpinner />
      </>
    );
  }

  if(isError) {
    return (
      <>
        <PageHeader back_url={`/domains/${id}`} text={i18n_t(`resources.domains.interactions.update_dns_servers`)} />
        {errorToComponent(error)}
      </>
    );
  }

  // Prepare DNSSEC keys from domain data
  const rawDnssecKeys = data?.domain?.dnssec_keys;
  const dnssecKeys = Array.isArray(rawDnssecKeys) ? rawDnssecKeys.map(key => ({
    flags: key.flags,
    protocol: key.protocol,
    algorithm: key.algorithm,
    public_key: key.public_key
  })) : [];

  const defaults = {
    dns_mode: data?.domain?.custom_dns_servers ? 'custom' : 'auto',
    nameserver1: data?.domain?.custom_dns_servers ? data?.domain?.dns_servers[0] : null,
    nameserver2: data?.domain?.custom_dns_servers ? data?.domain?.dns_servers[1] : null,
    nameserver3: data?.domain?.custom_dns_servers ? data?.domain?.dns_servers[2] : null,
    nameserver4: data?.domain?.custom_dns_servers ? data?.domain?.dns_servers[3] : null,
    nameserver5: data?.domain?.custom_dns_servers ? data?.domain?.dns_servers[4] : null,
    dnssec_keys: dnssecKeys,
    dnssec_enabled: dnssecKeys.length > 0
  };

  return (
    <GenericInteraction
      namespace="usercp"
      resource_name="domains"
      defaults={defaults}
      form_namespace="dns_servers"
      action="update_dns_servers"
      form_url={`/usercp/domains/${props.match.params.domain_id}/dns_servers`}
      method="post"
      button_text="DNS-Einstellungen speichern"
      icon_name="globe"
      onSuccess={onSuccess}
      onValidation={onValidation}
      id={props.match.params.domain_id}
      form_component={() => (<Form />)}
    />
  );
};

export default UpdateDnsServers;
